import { Injectable } from '@angular/core';
import { AiService, ImageEnhancementResult } from './ai.service';
import { S3Service } from './s3.service';

export type EnhancementType = 'quality' | 'regenerate' | 'add_people' | 'clear_weather' | 'custom';

@Injectable({
  providedIn: 'root',
})
export class AiImageEnhancementService {
  constructor(
    private aiService: AiService,
    private s3Service: S3Service,
  ) {}

  /**
   * Base64 문자열을 Blob으로 변환
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * 이미지 보정 및 S3 업로드
   * @param imageUrl 원본 이미지 URL
   * @param type 보정 타입
   * @param customPrompt 커스텀 프롬프트 (type이 'custom'인 경우)
   * @returns 보정된 이미지의 S3 URL
   */
  async enhanceAndUpload(imageUrl: string, type: EnhancementType, customPrompt?: string): Promise<string> {
    // AI 서비스를 통해 이미지 보정
    const result: ImageEnhancementResult = await this.aiService.enhanceImage(imageUrl, type, customPrompt);

    // Base64를 Blob으로 변환
    const blob = this.base64ToBlob(result.enhancedImageBase64, result.mimeType);

    // 원본 이미지 파일명 추출
    const originalFileName = imageUrl.split('/').pop()?.split('?')[0] || 'image';
    const originalExtension = originalFileName.split('.').pop() || 'jpg';

    // 새 파일명 생성
    const randomString = Math.random().toString(36).substring(2, 8);
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:.TZ]/g, '')
      .slice(0, 17);
    const enhancedFileName = `meetings-enhanced-${timestamp}-${randomString}.${originalExtension}`;

    // S3에 업로드
    const file = new File([blob], enhancedFileName, { type: blob.type });
    const uploadResult = await this.s3Service.uploadFile(file, 'meetings');

    if (!uploadResult.url) {
      throw new Error('S3 upload failed: No URL returned');
    }

    return uploadResult.url;
  }
}
